/**
 * Responsive utilities for consistent breakpoints and touch interactions
 */
import React from 'react';

// Standard breakpoints (matching Tailwind defaults)
export const breakpoints = {
  sm: 640,   // Small devices (landscape phones)
  md: 768,   // Medium devices (tablets)
  lg: 1024,  // Large devices (desktops)
  xl: 1280,  // Extra large devices
  '2xl': 1536 // 2X Extra large devices
};

// Touch-friendly sizing
export const touchTargets = {
  minimum: 44,    // Minimum touch target size (44px)
  comfortable: 48, // Comfortable touch target size
  large: 56       // Large touch target size
};

// Responsive grid configurations
export const gridConfigs = {
  cards: {
    mobile: 'grid-cols-1',
    tablet: 'sm:grid-cols-2',
    desktop: 'lg:grid-cols-3',
    wide: 'xl:grid-cols-4'
  },
  stats: {
    mobile: 'grid-cols-1',
    tablet: 'sm:grid-cols-2',
    desktop: 'lg:grid-cols-4'
  },
  form: {
    mobile: 'grid-cols-1',
    tablet: 'md:grid-cols-2',
    desktop: 'lg:grid-cols-3'
  },
  navigation: {
    mobile: 'grid-cols-2',
    tablet: 'grid-cols-4',
    desktop: 'flex'
  }
};

// Responsive spacing
export const spacing = {
  container: {
    mobile: 'px-4',
    tablet: 'sm:px-6',
    desktop: 'lg:px-8'
  },
  section: {
    mobile: 'py-6',
    tablet: 'sm:py-8',
    desktop: 'lg:py-12'
  },
  gap: {
    mobile: 'gap-4',
    tablet: 'sm:gap-6',
    desktop: 'lg:gap-8'
  }
};

// Typography scales
export const typography = {
  heading: {
    mobile: 'text-2xl',
    tablet: 'sm:text-3xl',
    desktop: 'lg:text-4xl'
  },
  subheading: {
    mobile: 'text-lg',
    tablet: 'sm:text-xl',
    desktop: 'lg:text-2xl'
  },
  body: {
    mobile: 'text-sm',
    tablet: 'sm:text-base'
  }
};

// Media query hooks for JavaScript
export const useMediaQuery = (query) => {
  const [matches, setMatches] = React.useState(
    typeof window !== 'undefined' ? window.matchMedia(query).matches : false
  );

  React.useEffect(() => {
    if (typeof window === 'undefined') return;
    const mediaQuery = window.matchMedia(query);
    const handler = (event) => setMatches(event.matches);
    
    mediaQuery.addEventListener('change', handler);
    return () => mediaQuery.removeEventListener('change', handler);
  }, [query]);

  return matches;
};

// Predefined media queries
export const mediaQueries = {
  mobile: `(max-width: ${breakpoints.sm - 1}px)`,
  tablet: `(min-width: ${breakpoints.sm}px) and (max-width: ${breakpoints.lg - 1}px)`,
  desktop: `(min-width: ${breakpoints.lg}px)`,
  touch: '(hover: none) and (pointer: coarse)',
  mouse: '(hover: hover) and (pointer: fine)',
  reducedMotion: '(prefers-reduced-motion: reduce)',
  darkMode: '(prefers-color-scheme: dark)'
};

// Responsive class builders
export const buildResponsiveClasses = (config) => {
  return Object.values(config).join(' ');
};

// Touch gesture utilities
export const touchGestures = {
  swipeThreshold: 50,    // Minimum distance for swipe
  tapTimeout: 300,       // Maximum time for tap
  longPressTimeout: 500, // Minimum time for long press
  
  // Detect swipe direction
  getSwipeDirection: (startX, startY, endX, endY) => {
    const deltaX = endX - startX;
    const deltaY = endY - startY;
    const absDeltaX = Math.abs(deltaX);
    const absDeltaY = Math.abs(deltaY);
    
    if (Math.max(absDeltaX, absDeltaY) < touchGestures.swipeThreshold) {
      return null; // Not a swipe
    }
    
    if (absDeltaX > absDeltaY) {
      return deltaX > 0 ? 'right' : 'left';
    } else {
      return deltaY > 0 ? 'down' : 'up';
    }
  }
};

// Accessibility utilities
export const a11y = {
  // Focus management
  trapFocus: (element) => {
    const focusableElements = element.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    const firstElement = focusableElements[0];
    const lastElement = focusableElements[focusableElements.length - 1];
    
    const handleTabKey = (e) => {
      if (e.key === 'Tab') {
        if (e.shiftKey) {
          if (document.activeElement === firstElement) {
            lastElement.focus();
            e.preventDefault();
          }
        } else {
          if (document.activeElement === lastElement) {
            firstElement.focus();
            e.preventDefault();
          }
        }
      }
    };
    
    element.addEventListener('keydown', handleTabKey);
    return () => element.removeEventListener('keydown', handleTabKey);
  },
  
  // Announce to screen readers
  announce: (message, priority = 'polite') => {
    const announcer = document.createElement('div');
    announcer.setAttribute('aria-live', priority);
    announcer.setAttribute('aria-atomic', 'true');
    announcer.className = 'sr-only';
    announcer.textContent = message;
    
    document.body.appendChild(announcer);
    setTimeout(() => document.body.removeChild(announcer), 1000);
  }
};

// Performance utilities
export const performance = {
  // Debounce function calls
  debounce: (func, wait) => {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  },
  
  // Throttle function calls
  throttle: (func, limit) => {
    let inThrottle;
    return function executedFunction(...args) {
      if (!inThrottle) {
        func.apply(this, args);
        inThrottle = true;
        setTimeout(() => inThrottle = false, limit);
      }
    };
  },
  
  // Intersection Observer for lazy loading
  createIntersectionObserver: (callback, options = {}) => {
    const defaultOptions = {
      root: null,
      rootMargin: '50px',
      threshold: 0.1
    };
    
    return new IntersectionObserver(callback, { ...defaultOptions, ...options });
  }
};

export default {
  breakpoints,
  touchTargets,
  gridConfigs,
  spacing,
  typography,
  mediaQueries,
  buildResponsiveClasses,
  touchGestures,
  a11y,
  performance
};